/**
 * KHIYARSA (Phase 2): typed client for the khiyarsa-api accounts/projects
 * backend (same-origin /api/app, session cookie auth). See
 * PATCHES.khiyarsa.md §8.
 */

const BASE = "/api/app";

export class ApiError extends Error {
  status: number;
  code: string;
  payload: any;

  constructor(status: number, code: string, payload?: any) {
    super(`${status} ${code}`);
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

export interface CloudUser {
  id: string;
  email: string;
  displayName: string | null;
}

export interface ProjectMeta {
  id: string;
  title: string;
  contentRevision: number;
  metadataRevision: number;
  sceneVersion: number | null;
  sceneSize: number;
  hasThumbnail: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CloudScene {
  elements: any[];
  appState: Record<string, any>;
}

export interface CloudFileMeta {
  id: string;
  mimeType: string;
  size: number;
}

const request = async (
  method: string,
  path: string,
  body?: unknown,
): Promise<any> => {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: body !== undefined ? { "Content-Type": "application/json" } : {},
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    throw new ApiError(0, "network_error");
  }
  let payload: any = null;
  try {
    payload = await res.json();
  } catch {
    // non-JSON (e.g. nginx 429/502 pages)
  }
  if (!res.ok) {
    throw new ApiError(res.status, payload?.error || `http_${res.status}`, payload);
  }
  return payload;
};

export const cloudApi = {
  // auth
  register: (email: string, password: string, displayName?: string) =>
    request("POST", "/auth/register", { email, password, displayName }) as Promise<{ user: CloudUser }>,
  login: (email: string, password: string) =>
    request("POST", "/auth/login", { email, password }) as Promise<{ user: CloudUser }>,
  logout: () => request("POST", "/auth/logout"),
  me: async (): Promise<CloudUser | null> => {
    try {
      const res = await request("GET", "/auth/me");
      return res.user;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        return null;
      }
      throw error;
    }
  },

  // projects
  listProjects: (trashed = false, query?: string) =>
    request(
      "GET",
      trashed
        ? "/projects/trash"
        : `/projects${query ? `?q=${encodeURIComponent(query)}` : ""}`,
    ) as Promise<{ projects: ProjectMeta[] }>,
  duplicateProject: (id: string) =>
    request("POST", `/projects/${id}/duplicate`) as Promise<{ project: ProjectMeta }>,
  accountUsage: () =>
    request("GET", "/account/usage") as Promise<{
      projects: number;
      projectsLimit: number;
      trashed: number;
      sceneBytes: number;
      fileBytes: number;
      fileBytesLimit: number;
    }>,
  createProject: (opts: {
    title: string;
    scene?: CloudScene;
    sceneVersion?: number;
    thumbnail?: string;
  }) =>
    request("POST", "/projects", opts) as Promise<{
      project: ProjectMeta;
      missingFiles: string[];
    }>,
  getProject: (id: string) =>
    request("GET", `/projects/${id}`) as Promise<{
      project: ProjectMeta;
      scene: CloudScene | null;
      files: CloudFileMeta[];
    }>,
  renameProject: (id: string, title: string) =>
    request("PUT", `/projects/${id}`, { title }) as Promise<{ project: ProjectMeta }>,
  saveProject: (
    id: string,
    opts: {
      baseRevision?: number;
      force?: boolean;
      sceneVersion: number;
      scene: CloudScene;
      thumbnail?: string;
    },
  ) =>
    request("POST", `/projects/${id}/save`, opts) as Promise<{
      revision: number;
      savedAt: string;
      missingFiles: string[];
    }>,
  deleteProject: (id: string) => request("DELETE", `/projects/${id}`),
  restoreProject: (id: string) => request("POST", `/projects/${id}/restore`),
  purgeProject: (id: string) => request("DELETE", `/projects/${id}/purge`),

  // revisions
  listRevisions: (id: string) =>
    request("GET", `/projects/${id}/revisions`) as Promise<{
      revisions: {
        id: number;
        contentRevision: number;
        sceneSize: number;
        label: string | null;
        createdAt: string;
      }[];
    }>,
  restoreRevision: (id: string, revisionId: number) =>
    request("POST", `/projects/${id}/revisions/${revisionId}/restore`) as Promise<{
      revision: number;
      savedAt: string;
      scene: CloudScene | null;
    }>,

  // files
  uploadFile: async (projectId: string, fileId: string, dataURL: string) => {
    const blob = await (await fetch(dataURL)).blob();
    const res = await fetch(`${BASE}/projects/${projectId}/files/${fileId}`, {
      method: "PUT",
      headers: { "Content-Type": blob.type || "application/octet-stream" },
      body: blob,
    });
    if (!res.ok) {
      let payload: any = null;
      try {
        payload = await res.json();
      } catch {}
      throw new ApiError(res.status, payload?.error || `http_${res.status}`, payload);
    }
  },
  downloadFile: async (
    projectId: string,
    fileId: string,
  ): Promise<{ dataURL: string; mimeType: string } | null> => {
    const res = await fetch(`${BASE}/projects/${projectId}/files/${fileId}`);
    if (!res.ok) {
      return null;
    }
    const blob = await res.blob();
    const dataURL = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return { dataURL, mimeType: blob.type };
  },

  thumbnailUrl: (projectId: string) => `${BASE}/projects/${projectId}/thumbnail`,
};
